import React, { ReactElement } from 'react';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import {
  APIPill,
  MLPill,
  Metadata,
  getPageName as libraryGetPageName,
} from '@fullstory/library-ui';
import { Box } from '@fullstory/components';
import { PageAPITooltipContent } from '../components/PageAPITooltipContent';
import { intl } from '@fullstory/intl';
import { AutocompleteRequest } from '@fullstory/proto/fs/services/paige';
import { int64 } from '@fullstory/proto/google/protobuf';

interface PageInformation {
  domain: string;
  path: string;
}

export const getPageInformation = (canonicalPageUrl: string): PageInformation => {
  const hasNoProtocol = canonicalPageUrl.indexOf('//') == -1;
  const url = hasNoProtocol ? canonicalPageUrl : canonicalPageUrl.split('//').slice(1).join();
  const splitUrl = url.split('/');
  const hostNames = splitUrl[0]!.split(',').map(d => {
    if (d[0] == '[') {
      return d.slice(1);
    } else if (d[d.length - 1] == ']') {
      return d.slice(0, -1);
    }
    return d;
  });
  let domain: string;
  if (hostNames.length > 1) {
    domain = intl.formatMessage(
      {
        defaultMessage: '{hostName} (and {hostNameCount, number} more)',
        id: 'p19ldW',
      },
      {
        hostName: hostNames[0],
        hostNameCount: hostNames.length - 1,
      },
    );
  } else {
    domain = hostNames[0]!;
  }
  const path = `/${splitUrl.slice(1).join('/')}`;
  return { domain, path };
};

export const getPageMetadata = (page: PageDefinition): Metadata => {
  const isSolelyApiDefined = page.labels.length > 0 && page.patterns.length === 0;

  return {
    owner: isSolelyApiDefined
      ? intl.formatMessage({ defaultMessage: 'Fullstory API', id: 'NJDgAL' })
      : undefined,
    createdAt: page.createdAt,
    createdBy: page.createdBy,
    updatedAt: page.updatedAt,
    updatedBy: page.updatedBy,
    rightAdornment: isSolelyApiDefined ? (
      <APIPill tooltipContent={<PageAPITooltipContent />} />
    ) : page.isUserDefined ? undefined : (
      <MLPill />
    ),
  };
};

export const getPageName = libraryGetPageName;

export const getPageAdornment = (page: PageDefinition): ReactElement | null => {
  // special case for page def zero
  if (page.id === 0) return null;

  // labels field should take precedence over the isUserDefined field
  const isSolelyApiDefined = page.labels.length > 0 && page.patterns.length === 0;
  if (isSolelyApiDefined) {
    return (
      <Box marginLeft={16}>
        <APIPill tooltipContent={<PageAPITooltipContent />} />
      </Box>
    );
  }

  if (!page.isUserDefined) {
    return (
      <Box marginLeft={16}>
        <MLPill />
      </Box>
    );
  }

  return null;
};

export function buildHostnameReq(orgId: string): AutocompleteRequest {
  return {
    orgId,
    path: '',
    scope: AutocompleteRequest.Scope.SCOPE_HOST,
    limit: '0' as int64,
    domain: '',
    fragment: '',
  };
}

export function buildPathReq(
  orgId: string,
  path: string,
  fragment: string,
  domain: string,
  scope: AutocompleteRequest.Scope,
): AutocompleteRequest {
  return {
    orgId,
    domain: domain !== '*' ? domain : '',
    path,
    fragment,
    scope,
    limit: '0' as int64,
  };
}
